import type { ComponentType } from "react";
import {
	AgentsChipIcon,
	AutomationsIcon,
	ConnectorsIcon,
	HomeIcon,
	MemoryLayersIcon,
} from "@/components/ui/lobu-nav-icons";
import { isReservedOwnerSegment } from "@/lib/reserved";

/** Any component that renders a `className`-styled glyph (lucide, brand mark). */
export type SectionIcon = ComponentType<{ className?: string }>;

/**
 * Owner-scoped nav sections — one icon + label shared by the sidebar, the
 * narrow top bar, and page headers. Sections render in this order: where data
 * comes from, then what acts on it.
 *
 *  - **Home** → the workspace home (`/`), its activity: one stream with the
 *    items that need the user inline (`/events` renders the same stream).
 *  - **Connectors** → connectors + synthesized providers/sandboxes
 *  - **Memory** → entity types/records (`/data`): the schema map, plus a tab
 *    per workspace-attached view
 *  - **Agents** → the workspace's agents (`/agents`) and their chats (`/chat`):
 *    managed agents plus the connected external apps that call in
 *  - **Automations** → org-wide automations (`/automations`) — automations are
 *    entity-anchored and agentless-capable, so they live at the workspace
 *    level, not under agents
 */
export const OWNER_SECTIONS = {
	activity: {
		icon: HomeIcon,
		label: "Home",
		/** Router template for the workspace home (the activity surface). */
		to: "/$owner" as const,
		search: {} as Record<string, unknown>,
		/** Path segment under `/$owner/` used for matching. */
		segment: "activity",
	},
	connectors: {
		icon: ConnectorsIcon,
		label: "Connectors",
		to: "/$owner/connectors",
		search: {} as Record<string, unknown>,
		segment: "connectors",
	},
	context: {
		icon: MemoryLayersIcon,
		label: "Memory",
		to: "/$owner/data",
		search: {} as Record<string, unknown>,
		segment: "data",
	},
	agents: {
		icon: AgentsChipIcon,
		label: "Agents",
		to: "/$owner/agents",
		search: {} as Record<string, unknown>,
		segment: "agents",
	},
	automations: {
		icon: AutomationsIcon,
		label: "Automations",
		to: "/$owner/automations",
		search: {} as Record<string, unknown>,
		segment: "automations",
	},
} as const satisfies Record<
	string,
	{
		icon: SectionIcon;
		label: string;
		to: string;
		search: Record<string, unknown>;
		segment: string;
	}
>;

export type OwnerSectionKey = keyof typeof OWNER_SECTIONS;

/** Owner-level static route segments that must never read as an entity type. */
const OWNER_STATIC_SEGMENTS = new Set<string>([
	...(Object.keys(OWNER_SECTIONS) as OwnerSectionKey[]).map(
		(key) => OWNER_SECTIONS[key].segment,
	),
	// `/events` is the explicit all-activity Events stream, not an entity-type path.
	"events",
	// Chat is an Agents child surface, not an entity type.
	"chat",
	// `/$owner/entity-types` is a static settings route, not an owner-nav
	// segment, so it is absent from OWNER_ROUTE_SEGMENTS — exclude it here too.
	"entity-types",
]);

function ownerFirstSegment(
	owner: string,
	pathname: string,
): string | undefined {
	const base = `/${owner}/`;
	if (!pathname.startsWith(base)) return undefined;
	return pathname.slice(base.length).split("/")[0] || undefined;
}

/**
 * The entity type a path is under (`/{owner}/<type>[/…]`: the list, a record,
 * or either one's `/-/views/<key>`), still URL-encoded, or undefined when the
 * first segment is an owner route rather than a type.
 */
export function entityTypeSegment(
	owner: string,
	pathname: string,
): string | undefined {
	const first = ownerFirstSegment(owner, pathname);
	if (
		!first ||
		OWNER_STATIC_SEGMENTS.has(first) ||
		isReservedOwnerSegment(first)
	)
		return undefined;
	return first;
}

/** The type map (`/data`) or a first-class entity-type path (`/{owner}/<type>[/…]`). */
export function isSchemaPath(owner: string, pathname: string): boolean {
	const first = ownerFirstSegment(owner, pathname);
	if (first === OWNER_SECTIONS.context.segment)
		return !pathname.includes("/-/views/");
	return entityTypeSegment(owner, pathname) !== undefined;
}

/**
 * Whether a sidebar section is active for the current path.
 *
 * Memory (Context) additionally lights on first-class entity-type paths
 * (`/{owner}/<type>` and `/{owner}/<type>/<slug>`): entity types are path
 * segments now, not `/data?type=` scopes. Any first segment that is neither
 * a static owner route nor a reserved owner segment is an entity-type path.
 */
export function isOwnerSectionActive(
	key: OwnerSectionKey,
	owner: string,
	pathname: string,
): boolean {
	// Activity is the workspace home, so it lights on the bare org root, the
	// explicit all-activity `/events` stream (including one event's page), and
	// the schema its toolbar opens: the type map and each type's pages. A
	// workspace view (`/data/-/views/<key>`) is its own sidebar entry.
	if (key === "activity") {
		const base = `/${owner}`;
		if (pathname === base || pathname === `${base}/`) return true;
		const first = ownerFirstSegment(owner, pathname);
		if (first === "events") return true;
		return isSchemaPath(owner, pathname);
	}
	// An agent's chat belongs to Agents.
	if (key === "agents") {
		const first = ownerFirstSegment(owner, pathname);
		if (first === "chat") return true;
	}
	if (key === "context") {
		const first = ownerFirstSegment(owner, pathname);
		if (first === OWNER_SECTIONS.context.segment) return true;
		return entityTypeSegment(owner, pathname) !== undefined;
	}
	const base = `/${owner}/${OWNER_SECTIONS[key].segment}`;
	return (
		pathname === base ||
		pathname === `${base}/` ||
		pathname.startsWith(`${base}/`)
	);
}
